import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, Printer, UserPlus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const emptyEmp = { name: "", department: "", designation: "", joining_date: "", salary: 20000, mobile: "" };

export default function HRMS() {
  const { user } = useAuth();
  const canManage = ["admin", "super_admin"].includes(user?.role);
  const [emps, setEmps] = useState([]);
  const [att, setAtt] = useState([]);
  const [pay, setPay] = useState([]);
  const [my, setMy] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [empOpen, setEmpOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [empForm, setEmpForm] = useState(emptyEmp);
  const nav = useNavigate();

  const load = () => {
    api.get("/employees").then((r) => setEmps(r.data));
    api.get("/attendance").then((r) => setAtt(r.data));
    api.get("/payroll", { params: { month } }).then((r) => setPay(r.data));
    api.get("/attendance/me").then((r) => setMy(r.data)).catch(() => setMy(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const punch = async (employee_id, kind) => { await api.post("/attendance/punch", { employee_id, kind }); toast.success(kind === "punch_in" ? "Punched In" : "Punched Out"); load(); };
  const selfPunch = async (kind) => {
    try { const { data } = await api.post("/attendance/self-punch", { kind }); toast.success(`${kind === "punch_in" ? "In" : "Out"} · ${data.employee}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No employee linked to your login"); }
  };
  const generatePayroll = async () => { await api.post("/payroll/generate", { month }); toast.success(`Payroll generated for ${month}`); load(); };

  const saveEmp = async () => {
    if (!empForm.name || !empForm.department) return toast.error("Name and department required");
    try {
      if (editing) await api.put(`/employees/${editing.id}`, empForm);
      else await api.post("/employees", empForm);
      toast.success(editing ? "Updated" : "Employee added");
      setEmpOpen(false); setEditing(null); setEmpForm(emptyEmp); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delEmp = async (e) => {
    if (!window.confirm(`Remove ${e.name}?`)) return;
    await api.delete(`/employees/${e.id}`); toast.success("Removed"); load();
  };
  const editEmp = (e) => { setEditing(e); setEmpForm({ ...emptyEmp, ...e }); setEmpOpen(true); };

  const today = new Date().toISOString().slice(0, 10);
  const todayAtt = (empId) => att.find((a) => a.employee_id === empId && a.date === today);
  const myTodayAtt = my?.records?.find((a) => a.date === today);

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">HRMS · Attendance · Payroll</h1>
          <p className="text-sm text-slate-500">Staff records, live punch, monthly payroll & payslips.</p>
        </div>
      </div>

      {/* Self Punch Card - visible for any user with linked employee */}
      {my?.employee && (
        <div className="bg-blue-50 border border-blue-200 p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm font-display font-semibold">
            {my.employee.name.split(" ").map((s) => s[0]).slice(0,2).join("")}
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">{my.employee.name}</div>
            <div className="text-xs text-slate-600">{my.employee.designation} · {my.employee.department} · {my.employee.emp_code}</div>
            {myTodayAtt && (
              <div className="text-[11px] mt-1 font-mono flex gap-2">
                {myTodayAtt.punch_in && <span className="text-teal-700">IN {new Date(myTodayAtt.punch_in).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span>}
                {myTodayAtt.punch_out && <span className="text-red-700">OUT {new Date(myTodayAtt.punch_out).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</span>}
                {myTodayAtt.hours > 0 && <span className="text-slate-700">Hours: {myTodayAtt.hours}</span>}
              </div>
            )}
          </div>
          <Button data-testid="self-punch-in" size="sm" onClick={() => selfPunch("punch_in")} disabled={!!myTodayAtt?.punch_in} className="rounded-sm bg-teal-700 hover:bg-teal-800"><LogIn className="w-4 h-4 mr-1"/>Punch In</Button>
          <Button data-testid="self-punch-out" size="sm" onClick={() => selfPunch("punch_out")} disabled={!myTodayAtt?.punch_in || !!myTodayAtt?.punch_out} className="rounded-sm bg-red-700 hover:bg-red-800"><LogOut className="w-4 h-4 mr-1"/>Punch Out</Button>
        </div>
      )}

      <Tabs defaultValue="attendance">
        <TabsList className="bg-white border">
          <TabsTrigger value="employees" data-testid="hrms-tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hrms-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="hrms-tab-payroll">Payroll</TabsTrigger>
          {my?.employee && <TabsTrigger value="me">My Attendance</TabsTrigger>}
        </TabsList>

        <TabsContent value="employees">
          <div className="bg-white border">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="label-tiny">{emps.length} employees</div>
              {canManage && <Button data-testid="add-emp-btn" onClick={() => { setEditing(null); setEmpForm(emptyEmp); setEmpOpen(true); }} size="sm" className="bg-blue-700 hover:bg-blue-800 rounded-sm"><UserPlus className="w-4 h-4 mr-1"/> Add Employee</Button>}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Code</th><th className="label-tiny">Name</th><th className="label-tiny">Department</th><th className="label-tiny">Designation</th><th className="label-tiny">Joining</th><th className="label-tiny text-right">Salary</th>{canManage && <th className="label-tiny w-24">Actions</th>}</tr></thead>
              <tbody>
                {emps.map((e) => (
                  <tr key={e.id} className="border-b">
                    <td className="p-2 font-mono text-xs">{e.emp_code}</td>
                    <td className="font-medium">{e.name}</td>
                    <td>{e.department}</td>
                    <td>{e.designation}</td>
                    <td className="text-xs">{e.joining_date}</td>
                    <td className="text-right font-mono">{fmtINR(e.salary)}</td>
                    {canManage && (
                      <td className="flex gap-1 py-2">
                        <button data-testid={`edit-emp-${e.emp_code}`} onClick={() => editEmp(e)} className="p-1 border rounded-sm hover:bg-slate-100"><Pencil className="w-3 h-3"/></button>
                        <button data-testid={`del-emp-${e.emp_code}`} onClick={() => delEmp(e)} className="p-1 border border-red-300 text-red-700 rounded-sm hover:bg-red-50"><Trash2 className="w-3 h-3"/></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attendance">
          <div className="bg-white border p-4">
            <div className="label-tiny mb-3">Today · {today}</div>
            <div className="grid grid-cols-2 gap-3">
              {emps.map((e) => {
                const a = todayAtt(e.id);
                return (
                  <div key={e.id} className="border border-slate-200 p-3 flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 text-blue-800 flex items-center justify-center rounded-sm font-display font-semibold">
                      {e.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{e.name}</div>
                      <div className="text-xs text-slate-500">{e.designation} · {e.department}</div>
                      {a && (
                        <div className="text-[11px] mt-1 font-mono flex gap-2">
                          {a.punch_in && <span className="text-teal-700">IN {new Date(a.punch_in).toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit"})}</span>}
                          {a.punch_out && <span className="text-red-700">OUT {new Date(a.punch_out).toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit"})}</span>}
                          {a.hours > 0 && <span className="text-slate-600">{a.hours}h</span>}
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="outline" data-testid={`punch-in-${e.emp_code}`} disabled={a?.punch_in} onClick={() => punch(e.id, "punch_in")} className="h-7 text-xs rounded-sm"><LogIn className="w-3 h-3 mr-1"/>In</Button>
                        <Button size="sm" variant="outline" data-testid={`punch-out-${e.emp_code}`} disabled={!a?.punch_in || a?.punch_out} onClick={() => punch(e.id, "punch_out")} className="h-7 text-xs rounded-sm"><LogOut className="w-3 h-3 mr-1"/>Out</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payroll">
          <div className="bg-white border p-4">
            <div className="flex items-center gap-3 mb-4">
              <label className="label-tiny">Month</label>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 h-9 px-2 text-sm" data-testid="payroll-month" />
              {canManage && <Button data-testid="generate-payroll" onClick={generatePayroll} className="bg-blue-700 hover:bg-blue-800 rounded-sm"><Clock className="w-4 h-4 mr-1"/> Generate Payroll</Button>}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Employee</th><th className="label-tiny">Days</th><th className="label-tiny">Hours</th><th className="label-tiny text-right">Gross</th><th className="label-tiny text-right">Earned</th><th className="label-tiny text-right">Deductions</th><th className="label-tiny text-right">Net Pay</th><th></th></tr></thead>
              <tbody>
                {pay.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="p-2"><div className="font-medium">{p.name}</div><div className="text-[10px] text-slate-500">{p.emp_code} · {p.designation}</div></td>
                    <td className="text-center">{p.days_present}/{p.working_days}</td>
                    <td className="text-center font-mono text-xs">{p.total_hours}</td>
                    <td className="text-right font-mono">{fmtINR(p.gross)}</td>
                    <td className="text-right font-mono">{fmtINR(p.earned)}</td>
                    <td className="text-right font-mono text-red-700">{fmtINR(p.deductions)}</td>
                    <td className="text-right font-mono font-semibold">{fmtINR(p.net)}</td>
                    <td><Button size="sm" variant="outline" onClick={() => window.open(`/payslip/${p.id}/print`, "_blank")} className="h-7 rounded-sm text-xs" data-testid={`payslip-${p.emp_code}`}><Printer className="w-3 h-3 mr-1"/>Payslip</Button></td>
                  </tr>
                ))}
                {pay.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-sm text-slate-500">No payroll for {month}. Click "Generate Payroll".</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {my?.employee && (
          <TabsContent value="me">
            <div className="bg-white border">
              <div className="p-3 border-b label-tiny">My Attendance History · Last 60 days</div>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny">Punch In</th><th className="label-tiny">Punch Out</th><th className="label-tiny text-right">Hours</th></tr></thead>
                <tbody>
                  {my.records.map((r) => (
                    <tr key={r.id} className="border-b">
                      <td className="p-2 font-mono text-xs">{r.date}</td>
                      <td className="font-mono text-xs">{r.punch_in ? new Date(r.punch_in).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) : "-"}</td>
                      <td className="font-mono text-xs">{r.punch_out ? new Date(r.punch_out).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) : "-"}</td>
                      <td className="text-right font-mono">{r.hours || 0}</td>
                    </tr>
                  ))}
                  {my.records.length === 0 && <tr><td colSpan="4" className="p-4 text-center text-sm text-slate-500">No attendance records yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["name","Name"],["department","Department"],["designation","Designation"],["mobile","Mobile"],["joining_date","Joining Date"]].map(([k,l]) => (
              <div key={k}><Label className="label-tiny">{l}</Label><Input type={k === "joining_date" ? "date" : "text"} value={empForm[k] || ""} onChange={(e) => setEmpForm({ ...empForm, [k]: e.target.value })} className="mt-1 h-9" data-testid={`emp-${k}`} /></div>
            ))}
            <div><Label className="label-tiny">Salary (₹)</Label><Input type="number" value={empForm.salary || ""} onChange={(e) => setEmpForm({ ...empForm, salary: parseFloat(e.target.value) || 0 })} className="mt-1 h-9" data-testid="emp-salary" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpOpen(false)}>Cancel</Button>
            <Button data-testid="emp-save" onClick={saveEmp} className="bg-blue-700 hover:bg-blue-800">{editing ? "Save Changes" : "Add Employee"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
